import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Copy, CheckCircle, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { TopBar } from '../components/TopBar';
import { LoadingOverlay } from '../components/LoadingOverlay';


export const MarketingScreen = () => {
  const { productId } = useParams();
  const [marketing, setMarketing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    generateMarketing();
  }, [productId]);

  const generateMarketing = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/marketing/generate`, {
        product_id: productId
      });
      setMarketing(res.data.marketing);
      setLoading(false);
      setGenerating(false);
    } catch (error) {
      toast.error('Failed to generate marketing content');
      setLoading(false);
      setGenerating(false);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) {
      toast.error('Nothing to copy');
      return;
    }
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`✓ ${label} copied to clipboard!`);
    }).catch(() => {
      toast.error('Failed to copy. Please try again.');
    });
  };

  if (generating) {
    return <LoadingOverlay message="Creating marketing content..." />;
  }

  if (loading || !marketing) {
    return (
      <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F2F5]">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <TopBar title="Marketing Content" showBack={true} />

        <div className="p-6 space-y-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-6 border border-green-100"
          >
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="text-[#25D366]" size={28} />
              <h2 className="text-2xl font-bold text-slate-800">
                Ready to Promote!
              </h2>
            </div>
            <p className="text-base text-[#667781] leading-relaxed">
              AI-generated content for your social media
            </p>
          </motion.div>

          {/* Reel Script */}
          {marketing.reel_script && marketing.reel_script.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="reel-script-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-800 text-xl">
                  Instagram Reel Script
                </h3>
                <button
                  onClick={() => copyToClipboard(
                    marketing.reel_script.map(s => 
                      `Scene ${s.scene}: ${s.shot}\n${s.text_overlay}`
                    ).join('\n\n'),
                    'Reel script'
                  )}
                  className="p-2.5 text-[#128C7E] active:scale-95 transition-transform"
                  data-testid="copy-reel-script-button"
                >
                  <Copy size={22} />
                </button>
              </div>
              <div className="space-y-3">
                {marketing.reel_script.map((scene, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-xl p-4">
                    <p className="text-base font-semibold text-[#128C7E] mb-1">
                      Scene {scene.scene} ({scene.duration})
                    </p>
                    <p className="text-base text-slate-700 mb-2 leading-relaxed">{scene.shot}</p>
                    <p className="text-base text-slate-600 italic leading-relaxed">
                      "{scene.text_overlay}"
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Captions */}
          {marketing.captions && marketing.captions.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="captions-section">
              <h3 className="font-bold text-slate-800 text-lg mb-4">
                Post Captions
              </h3>
              <div className="space-y-3">
                {marketing.captions.map((caption, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 rounded-xl p-4 flex items-start justify-between gap-3"
                  >
                    <p className="text-slate-700 flex-1">{caption}</p>
                    <button
                      onClick={() => copyToClipboard(caption, 'Caption')}
                      className="text-[#128C7E] flex-shrink-0"
                      data-testid={`copy-caption-${idx}-button`}
                    >
                      <Copy size={18} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hooks */}
          {marketing.hooks && marketing.hooks.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="hooks-section">
              <h3 className="font-bold text-slate-800 text-lg mb-4">
                Attention Hooks
              </h3>
              <div className="space-y-2">
                {marketing.hooks.map((hook, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl"
                  >
                    <span className="text-[#25D366] font-bold">{idx + 1}.</span>
                    <p className="text-slate-700 flex-1">{hook}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hashtags */}
          {marketing.hashtags && marketing.hashtags.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="hashtags-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-800 text-lg">
                  Hashtags
                </h3>
                <button
                  onClick={() => copyToClipboard(marketing.hashtags.join(' '), 'Hashtags')}
                  className="p-2 text-[#128C7E] active:scale-95 transition-transform"
                  data-testid="copy-hashtags-button"
                >
                  <Copy size={20} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {marketing.hashtags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Image Ideas */}
          {marketing.image_ideas && marketing.image_ideas.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid="image-ideas-section">
              <h3 className="font-bold text-slate-800 text-lg mb-4">
                Photo Ideas
              </h3>
              <ul className="space-y-2">
                {marketing.image_ideas.map((idea, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-slate-700">
                    <span className="text-[#128C7E] mt-1">•</span>
                    <span>{idea}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Posting Strategy */}
          {marketing.posting_strategy && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-100 p-5" data-testid="posting-strategy-section">
              <h3 className="font-bold text-slate-800 text-lg mb-3">
                Posting Strategy
              </h3>
              <p className="text-slate-700">{marketing.posting_strategy}</p>
            </div>
          )}

          {/* Regenerate Button */}
          <button
            onClick={generateMarketing}
            className="w-full h-16 bg-[#25D366] text-white rounded-xl text-xl font-bold flex items-center justify-center gap-3 transition-transform active:scale-95 shadow-lg shadow-green-200"
            data-testid="regenerate-button"
          >
            <Sparkles size={24} />
            Generate New Content
          </button>

          <p className="text-sm text-center text-[#667781]">
            Tip: Click "Copy" buttons above to easily share on social media
          </p>
        </div>
      </div>
    </div>
  );
};
